import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { navigationConfig } from '../config/navigationConfig';
import type { NavNode } from '../config/navigationConfig';

interface InlineNavProps {
  nodeId: string;
  onNodeSelect: (node: NavNode) => void;
  onReturnToMainMenu?: () => void;
}

// Helper to find a node by ID recursively
const findNodeById = (nodes: NavNode[], id: string): NavNode | undefined => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return undefined;
};

const InlineNav: React.FC<InlineNavProps> = ({ nodeId, onNodeSelect, onReturnToMainMenu }) => {
  const defaultCount = nodeId === 'platform' ? 8 : 7;
  const [visibleCount, setVisibleCount] = useState(defaultCount);
  // If nodeId is 'root', we show the top-level categories.
  let nodesToRender: NavNode[] = [];
  let headerText = 'Try asking about:';

  if (nodeId === 'root') {
    nodesToRender = navigationConfig;
  } else {
    const parentNode = findNodeById(navigationConfig, nodeId);
    if (parentNode && parentNode.children) {
      nodesToRender = parentNode.children;
      headerText = `Options for ${parentNode.title}:`;
      
      const isPublic = !localStorage.getItem('access_token') || window.location.pathname === '/login';
      if (isPublic && parentNode.id === 'support') {
        // The user requested only 'Raise Ticket' but also gave instructions for 'View Ticket Status' 
        // so we leave both and hide FAQ
        nodesToRender = nodesToRender.filter(n => n.id === 'sup-raise' || n.id === 'sup-status');
      }
    }
  }

  if (nodesToRender.length === 0) return null;

  const visibleNodes = nodesToRender.slice(0, visibleCount);
  const remaining = nodesToRender.length - visibleCount;
  const nextBatch = Math.min(4, remaining);
  const hasMore = remaining > 0;
  const hasLess = visibleCount > defaultCount && remaining === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col w-full mb-4 pl-10" // align with chat bubbles
    >
      <span className="text-sm text-gray-500 mb-2">{headerText}</span>
      <div className="flex flex-col gap-2">
        {visibleNodes.map((node) => (
          <button
            key={node.id}
            onClick={() => onNodeSelect(node)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-colors text-left text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100 w-fit max-w-full"
          >
            {node.icon && <span className="flex-shrink-0 opacity-70">{node.icon}</span>}
            <span className="truncate">{node.title}</span>
          </button>
        ))}
        
        {(hasMore || hasLess) && (
          <div className="flex items-center gap-3 pt-2">
            {hasMore && (
              <button
                onClick={() => setVisibleCount((prev) => prev + 4)}
                className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors bg-blue-50/50 hover:bg-blue-100/50 px-3 py-1.5 rounded-full"
              >
                <ChevronDown className="w-3.5 h-3.5" />
                Show {nextBatch} more &middot; {remaining} remaining
              </button>
            )}
            {hasLess && (
              <button
                onClick={() => setVisibleCount(defaultCount)}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-full"
              >
                <ChevronUp className="w-3.5 h-3.5" />
                Show Less
              </button>
            )}
          </div>
        )}

        {nodeId !== 'root' && (
          <div className="pt-2">
            <button
              onClick={() => {
                if (onReturnToMainMenu) {
                  onReturnToMainMenu();
                } else {
                  onNodeSelect({ id: 'root', title: 'Return to Main Menu', type: 'category' });
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-blue-200 bg-blue-50/80 hover:bg-blue-100 text-blue-700 text-xs font-semibold transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-fit cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5 text-blue-600" />
              Return to Main Menu
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default InlineNav;
